import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { PageToolbar, usePageTranslation } from "@/components/PageToolbar";

const INTRO = { title: "Genre Browser", sub: "Every genre, every country that shaped it, and the essential films to start with." };

export default function Genres() {
  const [genres, setGenres] = useState([]);

  useEffect(() => {
    api.get("/genres").then((r) => setGenres(r.data));
  }, []);

  const blocks = useMemo(() => INTRO, []);
  const { lang, tr, busy } = usePageTranslation(blocks, "genres-intro");

  return (
    <main data-testid="genres-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <p className="eyebrow">Forms of World Cinema</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
        <div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1c1512]">{tr("title", INTRO.title)}</h1>
          <p className="text-[#6b5d51] mt-3 max-w-2xl">{tr("sub", INTRO.sub)}</p>
          <div className="mt-4">
            <PageToolbar speakText={`${tr("title", INTRO.title)}. ${tr("sub", INTRO.sub)}`} busy={busy} lang={lang} />
          </div>
        </div>
        <Link to="/topics/genres-languages" data-testid="genres-topic-link" className="inline-flex items-center gap-2 border border-[#7d1a1a] text-[#7d1a1a] px-4 py-2 text-sm font-medium hover:bg-[#7d1a1a] hover:text-[#f6f1e7] transition-colors">
          <BookOpen className="w-4 h-4" /> Read the Genres & Languages guide
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {genres.map((g) => {
          const [c1, c2] = g.top[0]?.palette || ["#171210", "#7d1a1a"];
          return (
            <Link
              key={g.name}
              to={`/explore?genre=${encodeURIComponent(g.name)}`}
              data-testid={`genre-card-${g.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="poster-card bg-[#fbf8f1] border border-[#d8ccb8] group"
            >
              <div className="h-20 flex items-end justify-between p-4" style={{ background: `linear-gradient(120deg, ${c1}, ${c2})` }}>
                <span className="font-display font-bold text-2xl text-[#f6f1e7]">{g.name}</span>
                <span className="font-mono-label text-[10px] tracking-[0.18em] uppercase text-[#f6f1e7]/80">{g.count} film{g.count === 1 ? "" : "s"}</span>
              </div>
              <div className="p-5">
                <p className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#8a7c6d] mb-2">Essential Starts</p>
                <ul className="space-y-1.5">
                  {g.top.map((m) => (
                    <li key={m.id} className="text-sm text-[#2b241d]">
                      <span className="font-medium">{m.title}</span> <span className="text-[#8a7c6d]">({m.year}, {m.country})</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-[#8a7c6d] mt-3 line-clamp-1">Found in: {g.countries.join(", ")}</p>
                <p className="flex items-center gap-1 text-sm font-medium text-[#7d1a1a] mt-3">
                  Browse {g.name} films <ArrowRight className="w-4 h-4" />
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </main>
  );
}
